import React, { FC, useMemo } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import {
  DataGridPro,
  GridColDef,
  GridSortModel,
  LoadingOverlayPropsOverrides,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';

import { IAccrualBenefitTransactionDTO } from 'src/api/dtos';
import { DataGridLoadingOverlay, DataGridNoRowsOverlay, GenericDataGridStyle } from 'src/shared/components';
import { ArrowDownIcon, ArrowUpIcon, ChevronFilledDownIcon, ChevronFilledUpIcon } from 'src/shared/components/icons';
import { DATE_FORMAT } from 'src/shared/constants';
import { useTranslate } from 'src/shared/providers';
import { formatInputDuration } from 'src/shared/utils';
import { getDurationFormat } from 'src/store/user/user.selectors';

const GRID_ROW_HEIGHT = 36;
const DEFAULT_SORT_MODEL: GridSortModel = [{ field: 'date', sort: 'desc' }];

interface Props {
  rows: IAccrualBenefitTransactionDTO[];
  loading?: boolean;
}

export const AccrualDetailsGrid: FC<Props> = ({ rows, loading }) => {
  const { t } = useTranslate();
  const apiRef = useGridApiRef();
  const durationFormat = useSelector(getDurationFormat);

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'date',
        headerName: t('global.date'),
        minWidth: 140,
        flex: 1,
        renderCell: (params) => {
          return moment(params.value).format(DATE_FORMAT);
        },
      },
      {
        field: 'amountInSeconds',
        headerName: t('global.amount'),
        sortable: false,
        flex: 2,
        renderCell: (params) => {
          const BalanceIcon = params.value < 0 ? ArrowDownIcon : ArrowUpIcon;
          const duration = formatInputDuration(Math.abs(params.value), durationFormat, 'seconds');

          if (!duration.length) {
            return <Box sx={{ ml: '20px' }}>00:00</Box>;
          }

          return (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <BalanceIcon size={16} />
              <span>{duration}</span>
            </Stack>
          );
        },
      },
      {
        field: 'balanceInSeconds',
        headerName: t('global.balance'),
        sortable: false,
        flex: 2,
        renderCell: (params) => {
          const duration = formatInputDuration(params.value, durationFormat, 'seconds');
          return duration.length ? duration : '00:00';
        },
      },
      {
        field: 'who',
        headerName: t('accrualSummary.who'),
        sortable: false,
        flex: 2,
      },
      {
        field: 'action',
        headerName: t('global.action'),
        sortable: false,
        flex: 2,
      },
    ];
  }, [durationFormat, rows]);

  return (
    <Stack direction="row">
      <StyledDataGrid
        apiRef={apiRef}
        columns={columns}
        rows={rows}
        loading={loading}
        initialState={{ sorting: { sortModel: DEFAULT_SORT_MODEL } }}
        slots={{
          columnSortedAscendingIcon: ChevronFilledUpIcon,
          columnSortedDescendingIcon: ChevronFilledDownIcon,
          noRowsOverlay: DataGridNoRowsOverlay,
          loadingOverlay: DataGridLoadingOverlay,
        }}
        slotProps={{ loadingOverlay: { isEmpty: !rows.length } as LoadingOverlayPropsOverrides }}
        // Sorting
        sortingMode="client"
        sortingOrder={['asc', 'desc']}
        // Settings
        hideFooter
        disableColumnMenu
        disableColumnReorder
        disableMultipleRowSelection
        disableRowSelectionOnClick
        disableColumnResize
        rowSelection={false}
        isRowSelectable={() => false}
        // Style
        columnHeaderHeight={GRID_ROW_HEIGHT}
        rowHeight={GRID_ROW_HEIGHT}
        sx={{
          border: 'none',
          gap: 0,
          minHeight: 400,
          height: 400,
          '.MuiDataGrid-main': {
            borderRadius: '8px 8px 0 0',
          },
          // Hide horizontal scroll bar on data grid by setting height: 0
          '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
            width: 8,
            height: 0,
          },
        }}
      />
    </Stack>
  );
};

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
  ...GenericDataGridStyle(theme),
}));
